import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { VStack } from '@chakra-ui/react';
import { sortBy } from 'lodash';
import { ConfigInlineHeader, EventListItem, StateMessage } from '@/components';
import { useAuth, useConfig } from '@/store';
import { APP_CONFIG, DataTest, UserPhaseEventMapping } from '@/types';
import { ResolveDate } from '@/utils';
const { ConfigBlockType, ConfigComponentType, ScheduleDataFilterType, ScheduleDataFilterValue, ScheduleSortType } = APP_CONFIG.COMPONENTS;
const resolveDate = new ResolveDate();
export const applyDataFilter = (events, dataFilter) => {
    const filterMatchesEvent = (event, { key, type, value }) => {
        var _a;
        const matchedValue = (_a = event.meta.find((meta) => meta.key === key)) === null || _a === void 0 ? void 0 : _a.value;
        if (!matchedValue)
            return false;
        switch (type) {
            case ScheduleDataFilterValue.MATCH_BOTH_KEY_AND_VALUE:
                return matchedValue === value;
            case ScheduleDataFilterValue.MATCH_KEY_EXISTS:
                return true;
            case ScheduleDataFilterValue.MATCH_KEY_EXISTS_BUT_VALUE_DIFFERENT:
                return matchedValue !== value;
        }
    };
    return events.filter((event) => {
        if (dataFilter.filter_type === ScheduleDataFilterType.AND) {
            return dataFilter.values.every((filter) => filterMatchesEvent(event, filter));
        }
        if (dataFilter.filter_type === ScheduleDataFilterType.OR) {
            return dataFilter.values.some((filter) => filterMatchesEvent(event, filter));
        }
        return true;
    });
};
export const filterEvents = (events, block, now = new Date()) => {
    const filter = block.filter;
    // Event types that should be displayed in the schedule
    const eventTypes = filter.event_types.map((type) => type.toUpperCase());
    // If there is a data filter, apply it to the events
    const dataFilter = filter === null || filter === void 0 ? void 0 : filter.data_filter;
    const eventsAfterDataFilter = dataFilter ? applyDataFilter(events, dataFilter) : events;
    const scheduleDate = resolveDate.resolveScheduleDate(now);
    const filteredEvents = eventsAfterDataFilter.filter(({ event_date_time, type, window_end }) => {
        const eventType = type.toUpperCase();
        if (eventTypes.includes(eventType)) {
            const eventDate = new Date(event_date_time);
            const minDate = scheduleDate[filter === null || filter === void 0 ? void 0 : filter.after_date];
            const maxDate = scheduleDate[filter === null || filter === void 0 ? void 0 : filter.before_date];
            const isBeforeEnd = now < new Date(window_end);
            const isBetween = eventDate >= minDate && eventDate <= maxDate;
            return isBeforeEnd && isBetween;
        }
        return false;
    });
    return filteredEvents;
};
const eventTime = (event) => Date.parse(event.event_date_time);
const isCompletedQuestionnaire = (event) => event.type === UserPhaseEventMapping.QUESTIONNAIRE && event.completed;
const getFutureWindowStart = (event, now) => {
    if (event.type === UserPhaseEventMapping.VISIT)
        return null;
    const windowStart = Date.parse(event.window_start);
    return windowStart >= now.getTime() ? windowStart : null;
};
const sortDirectionKeys = {
    [ScheduleSortType.BACKEND_SORT_KEY]: ['sort_key'],
    [ScheduleSortType.LATEST]: [(event) => -eventTime(event), 'name'],
    [ScheduleSortType.OLDEST]: [eventTime, 'name']
};
export const sortEvents = (events, block, now = new Date()) => {
    var _a, _b;
    return sortBy(events, [
        isCompletedQuestionnaire,
        (event) => getFutureWindowStart(event, now) !== null,
        (event) => Boolean(event.is_dependency_locked),
        (event) => { var _a; return (_a = getFutureWindowStart(event, now)) !== null && _a !== void 0 ? _a : 0; },
        ...((_b = sortDirectionKeys[(_a = block.filter) === null || _a === void 0 ? void 0 : _a.sort_direction]) !== null && _b !== void 0 ? _b : [])
    ]);
};
export const applyLimit = (events, block) => {
    const { filter: { limit = Number.MAX_VALUE } = {} } = block;
    return events.slice(0, limit);
};
export const processScheduleEvents = (events, block, now = new Date()) => {
    const filteredEvents = filterEvents(events, block, now);
    const visibleEvents = applyLimit(sortEvents(filteredEvents, block, now), block);
    return { filteredEvents, visibleEvents };
};
export const ConfigSchedule = () => {
    const { getComponentConfig } = useConfig();
    const { state: { currentUserPhase, schedule } } = useAuth();
    const dashData = getComponentConfig(ConfigComponentType.DASHBOARD, currentUserPhase);
    const scheduleBlocks = dashData === null || dashData === void 0 ? void 0 : dashData.blocks.filter((block) => block.type === ConfigBlockType.SCHEDULE);
    const now = new Date();
    const processedScheduleBlocks = scheduleBlocks.map((block) => {
        const { filteredEvents, visibleEvents } = processScheduleEvents(schedule, block, now);
        return {
            allCompleteStateMessaging: block.allCompleteStateMessaging,
            allItemsComplete: filteredEvents.every(({ completed }) => completed),
            emptyStateMessaging: block.emptyStateMessaging,
            events: visibleEvents,
            scheduleHasEvents: visibleEvents.length > 0,
            header: block.header
        };
    });
    return (_jsx(_Fragment, { children: processedScheduleBlocks.map(({ allCompleteStateMessaging, allItemsComplete, emptyStateMessaging, events, scheduleHasEvents, header }, index) => {
            const HeaderComponent = header ? (_jsx(ConfigInlineHeader, Object.assign({}, header, { "data-test": DataTest.SCHEDULE_HEADER }), `schedule-${index}-header`)) : null;
            // The schedule block has no events to display and no custom empty state message configured.
            if (!scheduleHasEvents && !emptyStateMessaging) {
                return null;
            }
            // There are no events, but the config provides a message to show when the schedule is empty.
            if (!scheduleHasEvents && emptyStateMessaging) {
                return (_jsxs(VStack, Object.assign({ alignItems: 'flex-start', w: '100%' }, { children: [HeaderComponent, _jsx(StateMessage, { message: emptyStateMessaging })] }), `schedule-${index}`));
            }
            // The schedule contains events, but all have been completed, and a "completed" message is configured.
            if (allItemsComplete && allCompleteStateMessaging) {
                return (_jsxs(VStack, Object.assign({ alignItems: 'flex-start', w: '100%' }, { children: [HeaderComponent, _jsx(StateMessage, { message: allCompleteStateMessaging })] }), `schedule-${index}`));
            }
            // The normal case where there are events to show.
            return (_jsxs(VStack, Object.assign({ alignItems: 'flex-start', w: '100%' }, { children: [HeaderComponent, events.map((event) => {
                        return _jsx(EventListItem, Object.assign({}, event), event.id);
                    })] }), `schedule-${index}`));
        }) }));
};
